import { IErrorResponse } from 'typeScript/interfaces/IErrorMessage'
import { IKycStatus } from 'typeScript/interfaces/IKyc'
import styles from '../../pages/portal/Payout.module.scss'
import { t } from 'i18next'
import Badge, { BadgeState } from 'components/Badge'
import { isErrorResponse, mapKycEnumValue } from 'utils/common'
import Button from 'components/Button'
import { KycStatus, KycStatusInfo } from 'constants/KYCStatus'
import Constants from '../../constants/index'
import { useEffect, useState } from 'react'
import api from 'api/api'
import moment from 'moment'
import { useStore } from '@envision/ev-ui-component-library'
import InnerSpinner from 'components/InnerSpinner'
import tick from '../../assets/GreenTick.svg'

interface ApiResponse<T> {
  data: T
  status: number
  statusText: string
  headers: Record<string, string>
  config: Record<string, any>
}

function StatusKYC() {
  const [kycStatus, setKycStatus] = useState<IKycStatus | IErrorResponse>({
    kyc_status: KycStatus.REJECTED,
    kyc_error_message_code: KycStatusInfo.UNKNOWN_ERROR,
    last_status_update_date: '',
    message: '',
  })
  const [loadingKYCStatus, setLoadingKYCStatus] = useState(true)
  const [waitingForUpdate, setWaitingForUpdate] = useState(false)

  const [eventEmitter] = useStore.eventEmitter()

  const fetchKYCStatus = async () => {
    try {
      const resp = (await api.payout.getKYCStatus()) as ApiResponse<IKycStatus>
      if (!isErrorResponse(resp) && resp.data) {
        setKycStatus({
          kyc_status: mapKycEnumValue(KycStatus, resp.data.kyc_status),
          kyc_error_message_code: mapKycEnumValue(
            KycStatusInfo,
            resp.data.kyc_error_message_code
          ),
          last_status_update_date: moment(resp.data.last_status_update_date)
            .locale('de')
            .format('DD.MM.YYYY HH:mm'),
        })
      }
    } catch (error) {
      console.error(
        `[${new Date().toISOString()}] Failed to fetch KYC status:`,
        error
      )
    } finally {
      if (!waitingForUpdate) {
        setLoadingKYCStatus(false)
      }
    }
  }

  useEffect(() => {
    let mounted = true

    const handleKYCStatusUpdate = async (data: { state: string }) => {
      if (!mounted || !data.state) return

      setWaitingForUpdate(true)
      setLoadingKYCStatus(true)

      try {
        await fetchKYCStatus() // Fetch immediately without delay
      } catch (error) {
        console.error('Error fetching KYC status:', error)
      } finally {
        if (mounted) {
          setWaitingForUpdate(false)
          setLoadingKYCStatus(false)
        }
      }
    }

    fetchKYCStatus()
    eventEmitter.on('fetchKYCStatus', handleKYCStatusUpdate)

    return () => {
      mounted = false
      eventEmitter.off('fetchKYCStatus', handleKYCStatusUpdate)
    }
  }, [])

  if (isErrorResponse(kycStatus)) {
    return (
      <InnerSpinner isActive={loadingKYCStatus || waitingForUpdate}>
        <>
          <h3 className="mb-5 text-fg_primary">
            {t('payout:verification_information')}
          </h3>
          <div>{t('toast:something_went_wrong')}</div>
        </>
      </InnerSpinner>
    )
  }

  return (
    <InnerSpinner isActive={loadingKYCStatus || waitingForUpdate}>
      <>
        <div className="flex justify-between">
          <div>
            {t('payout:account_status')}
            {'  '}
            {kycStatus.kyc_status == KycStatus.PASSED && (
              <img
                width="25px"
                style={{ verticalAlign: 'middle' }}
                src={tick}
                alt=""
              />
            )}
          </div>

          <Badge
            state={
              kycStatus.kyc_status === KycStatus.REJECTED
                ? BadgeState.ERROR
                : kycStatus.kyc_status === KycStatus.FAILED
                  ? BadgeState.ERROR
                  : BadgeState.DEFAULT
            }
          >
            <p className="text-md">{t(`${kycStatus.kyc_status}`)}</p>
          </Badge>
        </div>
        <div className="py-4 pt-4">
          {kycStatus.kyc_error_message_code &&
            kycStatus.kyc_status !== KycStatus.PASSED &&
            kycStatus.kyc_status !== KycStatus.IN_PROGRESS && (
              <div>
                <b className="inline">{t('payout:kyc_info')}: </b>
                {t(`${kycStatus.kyc_error_message_code}`)}
              </div>
            )}
        </div>

        <p className={styles.date}>
          {t('payout:iban_creation_date')} {kycStatus.last_status_update_date}
        </p>

        {kycStatus.kyc_status !== KycStatus.PASSED &&
          kycStatus.kyc_status !== KycStatus.IN_PROGRESS &&
          kycStatus.kyc_status !== KycStatus.REJECTED && (
            <p
              className="mt-4 text-fg_primary"
              dangerouslySetInnerHTML={{
                __html: t('payout:contact_support', {
                  email: Constants.SUPPORT_EMAIL,
                  phone: Constants.CUSTOMER_SUPPORT_PHONE_NUMBER,
                }),
              }}
            />
          )}

        {kycStatus.kyc_status == KycStatus.REJECTED && (
          <p
            className="mt-4 text-fg_primary"
            dangerouslySetInnerHTML={{
              __html: t('payout:contact_support_when_rejected', {
                email: Constants.SUPPORT_EMAIL,
                phone: Constants.CUSTOMER_SUPPORT_PHONE_NUMBER,
              }),
            }}
          />
        )}

        {kycStatus.kyc_status == KycStatus.PASSED && (
          <p className="mt-8 text-fg_primary">
            {t('payout:successfully_verified')}
          </p>
        )}

        {kycStatus.kyc_status !== KycStatus.PASSED &&
          kycStatus.kyc_status !== KycStatus.REJECTED &&
          kycStatus.kyc_status !== KycStatus.IN_PROGRESS && (
            <div className="buttons">
              <Button
                onClick={() => {
                  eventEmitter.emit('fetchKYCUrl', { state: 'true' })
                  eventEmitter.emit('openModal', { id: 'kyc-modal' })
                }}
              >
                {t('payout:start_kyc_onboarding')}
              </Button>
            </div>
          )}
      </>
    </InnerSpinner>
  )
}

export default StatusKYC
